/* HAK noir sections — shared interactions.
 * Covers: dossier tabs (full APG keyboard model), PDP media gallery
 * (scroll-snap carousel with debounced announcements), variant radios
 * (price/availability sync + announcement via the price status region),
 * and the sticky bars (inert when off-screen, focus-safe hide).
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- dossier tabs (WAI-ARIA APG tabs pattern) ---------- */

  document.querySelectorAll('[data-hak-tabs]').forEach(function (root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
        var panel = panelFor(t);
        if (panel) panel.hidden = !selected;
      });
      if (focus) {
        tab.focus();
        if (typeof tab.scrollIntoView === 'function') {
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab, false);
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          select(next, true);
        }
      });
    });
  });

  /* ---------- PDP media gallery ---------- */

  document.querySelectorAll('[data-hak-gallery]').forEach(function (root) {
    var scroller = root.querySelector('[data-hak-slides]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-hak-thumb]'));
    var status = root.querySelector('[data-hak-gallery-status]');
    if (!scroller || !thumbs.length) return;

    var suppressAnnounce = false;
    var scrollTimer = null;

    function setCurrent(index, announce) {
      thumbs.forEach(function (t, n) {
        if (n === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (announce && status) {
        status.textContent = status.dataset.template
          .replace('[current]', index + 1)
          .replace('[total]', thumbs.length);
      }
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        var slide = scroller.children[index];
        if (!slide) return;
        suppressAnnounce = true; /* click already self-announces via aria-current */
        scroller.scrollTo({
          left: slide.offsetLeft - scroller.offsetLeft,
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
        });
        setCurrent(index, false);
      });
    });

    scroller.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var index = Math.round(scroller.scrollLeft / scroller.clientWidth);
          index = Math.max(0, Math.min(index, thumbs.length - 1));
          setCurrent(index, !suppressAnnounce);
          suppressAnnounce = false;
        }, 500);
      },
      { passive: true }
    );
  });

  /* ---------- variant radios ---------- */

  document.querySelectorAll('[data-hak-variants]').forEach(function (fieldset) {
    var form = fieldset.closest('form');
    if (!form) return;
    var sectionRoot = fieldset.closest('[data-hak-product]') || document;
    var priceNow = sectionRoot.querySelector('[data-hak-price-now]');
    var priceSaleLabel = sectionRoot.querySelector('[data-hak-price-sale-label]');
    var priceCompareGroup = sectionRoot.querySelector('[data-hak-price-compare-group]');
    var priceCompare = sectionRoot.querySelector('[data-hak-price-compare]');
    var atcPrices = sectionRoot.querySelectorAll('[data-hak-atc-price]');
    var plainPrices = sectionRoot.querySelectorAll('[data-hak-atc-price-plain]');
    var atcLabels = sectionRoot.querySelectorAll('[data-hak-atc-label]');
    var submits = sectionRoot.querySelectorAll('[name="add"]');

    fieldset.addEventListener('change', function (e) {
      var radio = e.target;
      if (radio.name !== 'id') return;
      var available = radio.dataset.available === 'true';
      var soldOutText = fieldset.dataset.soldOutText || 'Sold out';
      var addText = fieldset.dataset.addText || 'Add to cart';

      if (priceNow) priceNow.textContent = radio.dataset.price;
      var comparePrice = radio.dataset.comparePrice || '';
      if (priceCompare) priceCompare.textContent = comparePrice;
      if (priceCompareGroup) priceCompareGroup.hidden = comparePrice === '';
      if (priceSaleLabel) priceSaleLabel.hidden = comparePrice === '';
      if (priceNow) priceNow.classList.toggle('hak-price__now--sale', comparePrice !== '');
      atcPrices.forEach(function (el) {
        el.textContent = available ? '— ' + radio.dataset.price : '';
      });
      plainPrices.forEach(function (el) {
        el.textContent = radio.dataset.price;
      });
      atcLabels.forEach(function (el) {
        el.textContent = available ? addText : soldOutText;
      });
      submits.forEach(function (btn) {
        btn.disabled = !available;
      });
    });
  });

  /* ---------- sticky bars ---------- */

  document.querySelectorAll('[data-hak-sticky]').forEach(function (bar) {
    var target = document.querySelector(bar.dataset.hakSticky);
    if (!target || !('IntersectionObserver' in window)) return;

    function setVisible(visible) {
      if (!visible && bar.contains(document.activeElement)) {
        /* Never hide the bar out from under keyboard focus (2.4.11). */
        var fallback = target.querySelector('a, button, input, [tabindex]') || target;
        if (typeof fallback.focus === 'function') fallback.focus({ preventScroll: true });
      }
      bar.classList.toggle('is-visible', visible);
      bar.toggleAttribute('inert', !visible);
      if (visible) bar.removeAttribute('aria-hidden');
      else bar.setAttribute('aria-hidden', 'true');
    }

    var mode = bar.dataset.hakStickyMode || 'after'; /* 'after': show once target scrolled above */
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (mode === 'until') {
            /* landing: hide once the target section is reached */
            setVisible(!entry.isIntersecting && entry.boundingClientRect.top > 0);
          } else {
            /* pdp: show once the buy box has scrolled off the top */
            setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          }
        });
      },
      { threshold: 0 }
    ).observe(target);
  });
})();
